import { Component } from 'react';
import PropTypes from 'prop-types';
import { FaExclamationTriangle, FaRedo } from 'react-icons/fa';

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            retryCount: 0
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details for debugging
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({
            error,
            errorInfo,
            hasError: true
        });

        // Report error to monitoring service if available
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    handleRetry = () => {
        this.setState(prevState => ({
            hasError: false,
            error: null,
            errorInfo: null,
            retryCount: prevState.retryCount + 1
        }));
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default error UI
            return (
              <div
                className="min-h-[200px] flex flex-col items-center justify-center p-8 bg-red-50 border border-red-200 rounded-lg"
                role="alert"
                aria-live="assertive"
              >
                <FaExclamationTriangle
                  className="text-red-500 text-4xl mb-4"
                  aria-hidden="true"
                />
                <h2 className="text-xl font-semibold text-red-800 mb-2">
                  {this.props.title || "Something went wrong"}
                </h2>
                <p className="text-red-600 text-center mb-4 max-w-md">
                  {this.props.message ||
                    "An unexpected error occurred. Please try refreshing the page or contact support if the problem persists."}
                </p>

                {this.props.showRetry && this.state.retryCount < 3 && (
                  <button
                    type="button"
                    onClick={this.handleRetry}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 touch-manipulation"
                    aria-label="Retry loading this section"
                  >
                    <FaRedo className="text-sm" />
                    Try Again
                  </button>
                )}

                {process.env.NODE_ENV === "development" && this.state.error && (
                  <details className="mt-4 w-full max-w-2xl">
                    <summary className="cursor-pointer text-red-700 font-medium">
                      Error Details (Development)
                    </summary>
                    <pre className="mt-2 p-4 bg-red-100 rounded text-xs overflow-auto text-red-800">
                      {this.state.error.toString()}
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            );
        }

        return this.props.children;
    }
}

ErrorBoundary.propTypes = {
    children: PropTypes.node.isRequired,
    fallback: PropTypes.node,
    title: PropTypes.string,
    message: PropTypes.string,
    showRetry: PropTypes.bool,
    onError: PropTypes.func
};

ErrorBoundary.defaultProps = {
    showRetry: true
};

export default ErrorBoundary;